import { openModal } from '../navigationController';
import type { PlanCode, Root } from '../prototypeTypes';
import { ensureBusinessOverlayMounted } from '../businessOverlayLifecycle';

const resumePromptAttempts = new WeakMap<Root, symbol>();

/**
 * Present the "resume auto-renew before upgrading" prompt
 * (server error subscription_resume_required). Creem rejects an upgrade while
 * cancel_at_period_end is set, so the user must re-enable auto-renew first.
 * The modal is informational (no form input), so overlay click may close it.
 */
export function presentUpgradeResumePrompt(root: Root, nextPlan: PlanCode): void {
	ensureBusinessOverlayMounted(root, 'upgradeResumeModal');
	resumePromptAttempts.set(root, Symbol('upgrade-resume'));
	root.querySelectorAll<HTMLElement>('#upgradeResumeTargetPlan').forEach((element) => {
		element.textContent = nextPlan.charAt(0).toUpperCase() + nextPlan.slice(1);
	});
	// When the resume prompt is opened on top of the upgrade confirm modal
	// (step 2), fold it into the confirm modal's history entry instead of
	// pushing a new one. Otherwise the confirm modal's entry is orphaned when
	// it closes, and a later unconsumed popstate re-applies a stale URL (the
	// occasional jump to /membership-billing).
	const confirmOnTop = root.querySelector<HTMLElement>('#upgradeConfirmModal')?.classList.contains('active') === true;
	openModal(root, 'upgradeResumeModal', undefined, { pushHistory: !confirmOnTop });
}

export function ownsUpgradeResumePrompt(root: Root, attempt: symbol): boolean {
	return resumePromptAttempts.get(root) === attempt
		&& root.querySelector<HTMLElement>('#upgradeResumeModal')?.classList.contains('active') === true;
}

export function currentUpgradeResumePromptAttempt(root: Root): symbol {
	const attempt = resumePromptAttempts.get(root) ?? Symbol('upgrade-resume');
	resumePromptAttempts.set(root, attempt);
	return attempt;
}

export function clearUpgradeResumePrompt(root: Root): void {
	resumePromptAttempts.delete(root);
}
